import { post } from './api.js';
import { base64urlEncode, base64urlDecode } from './crypto.js';

export function isWebAuthnAvailable() {
  return !!(window.PublicKeyCredential && navigator.credentials);
}

function encodeCredential(cred) {
  const response = cred.response;
  const result = {
    id: cred.id,
    rawId: base64urlEncode(new Uint8Array(cred.rawId)),
    type: cred.type,
    response: {},
  };
  if (response.attestationObject) {
    result.response.attestationObject = base64urlEncode(new Uint8Array(response.attestationObject));
    result.response.clientDataJSON = base64urlEncode(new Uint8Array(response.clientDataJSON));
    if (cred.authenticatorAttachment) {
      result.authenticatorAttachment = cred.authenticatorAttachment;
    }
    const transports = response.getTransports ? response.getTransports() : [];
    result.response.transports = transports;
  }
  if (response.authenticatorData) {
    result.response.authenticatorData = base64urlEncode(new Uint8Array(response.authenticatorData));
    result.response.clientDataJSON = base64urlEncode(new Uint8Array(response.clientDataJSON));
    result.response.signature = base64urlEncode(new Uint8Array(response.signature));
    if (response.userHandle) {
      result.response.userHandle = base64urlEncode(new Uint8Array(response.userHandle));
    }
  }
  return result;
}

function decodePublicKeyOptions(options) {
  if (options.challenge) {
    options.challenge = base64urlDecode(options.challenge);
  }
  if (options.user && options.user.id) {
    options.user.id = base64urlDecode(options.user.id);
  }
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(c => ({
      ...c,
      id: base64urlDecode(c.id),
    }));
  }
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map(c => ({
      ...c,
      id: base64urlDecode(c.id),
      // Ensure transports are set so the browser knows to use the
      // platform authenticator (FaceID/TouchID) rather than showing
      // QR code / hardware key options.
      transports: c.transports && c.transports.length > 0
        ? c.transports
        : ['internal', 'hybrid'],
    }));
  }
  return options;
}

export async function startRegistration(name) {
  const beginResp = await post('/passkeys/register/begin', {});
  const publicKey = decodePublicKeyOptions(beginResp.publicKey);

  const createOptions = {
    publicKey,
  };
  // Add PRF extension hint
  createOptions.publicKey.extensions = {
    ...createOptions.publicKey.extensions,
    prf: {},
  };

  // Platform filters out USB/BLE keys. On iOS 17+, third-party
  // credential managers (Bitwarden, 1Password) still appear because
  // the OS treats them as platform authenticators. The user controls
  // which provider is used in Settings > Passwords > Password Options.
  createOptions.publicKey.authenticatorSelection = {
    ...createOptions.publicKey.authenticatorSelection,
    authenticatorAttachment: 'platform',
    residentKey: 'required',
    userVerification: 'required',
  };

  const credential = await navigator.credentials.create(createOptions);
  const encoded = encodeCredential(credential);

  // Check PRF support from extensions output
  const extResults = credential.getClientExtensionResults();
  const prfEnabled = !!(extResults.prf && extResults.prf.enabled);

  const finishResp = await post('/passkeys/register/finish', {
    state_id: beginResp.state_id,
    credential: encoded,
    name: name || '',
    prf_salt: beginResp.prf_salt,
  });

  return {
    ...finishResp,
    prf_enabled: prfEnabled,
    credential_id: encoded.rawId,
  };
}

export async function activatePrf(credentialId, prfSalt) {
  const beginResp = await post('/passkeys/activate-prf/begin', {
    credential_id: credentialId,
  });

  const publicKey = decodePublicKeyOptions(beginResp.publicKey);
  const saltBytes = base64urlDecode(prfSalt);

  const getOptions = {
    publicKey,
  };

  // Use eval (not evalByCredential) for broad compatibility — Safari
  // on iOS only supports eval. Since allowCredentials targets a single
  // credential here, eval is equivalent.
  getOptions.publicKey.extensions = {
    ...getOptions.publicKey.extensions,
    prf: {
      eval: { first: saltBytes },
    },
  };

  const assertion = await navigator.credentials.get(getOptions);
  const extResults = assertion.getClientExtensionResults();

  let prfOutput = null;
  if (extResults.prf && extResults.prf.results) {
    prfOutput = new Uint8Array(extResults.prf.results.first);
  }

  const encoded = encodeCredential(assertion);
  return {
    assertion: encoded,
    state_id: beginResp.state_id,
    prf_output: prfOutput,
  };
}

export async function finishActivatePrf(stateId, assertion, prfWrappedMasterKey) {
  return await post('/passkeys/activate-prf/finish', {
    state_id: stateId,
    credential: assertion,
    prf_wrapped_master_key: prfWrappedMasterKey,
  });
}

export async function authenticate(email) {
  const beginResp = await post('/passkeys/authenticate/begin', { email });
  const publicKey = decodePublicKeyOptions(beginResp.publicKey);

  const getOptions = {
    publicKey,
  };

  // Set up PRF eval for all credentials that have salts.
  // Prefer evalByCredential (allows per-credential salts), but fall back
  // to eval for browsers like Safari iOS that only support eval.
  if (beginResp.prf_salts && Object.keys(beginResp.prf_salts).length > 0) {
    const entries = Object.entries(beginResp.prf_salts);
    const evalByCredential = {};
    for (const [credId, saltB64] of entries) {
      evalByCredential[credId] = { first: base64urlDecode(saltB64) };
    }
    // eval uses first credential's salt as fallback — works when user
    // has a single passkey (common case) or all share the same salt.
    const fallbackSalt = base64urlDecode(entries[0][1]);
    getOptions.publicKey.extensions = {
      ...getOptions.publicKey.extensions,
      prf: {
        eval: { first: fallbackSalt },
        evalByCredential,
      },
    };
  }

  const assertion = await navigator.credentials.get(getOptions);
  const extResults = assertion.getClientExtensionResults();

  let prfOutput = null;
  if (extResults.prf && extResults.prf.results) {
    prfOutput = new Uint8Array(extResults.prf.results.first);
  }

  const encoded = encodeCredential(assertion);
  const finishResp = await post('/passkeys/authenticate/finish', {
    state_id: beginResp.state_id,
    credential: encoded,
  });

  return {
    ...finishResp,
    prf_output: prfOutput,
  };
}
